import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, Download, BookOpen, Eye } from 'lucide-react';

const PublicationsPage = () => {
  const publications = [
    {
      id: 1,
      title: 'The Complete Trademark Registration Guide 2024',
      description: 'A comprehensive 50-page guide covering everything you need to know about trademark registration, from initial search to final approval.',
      type: 'eBook',
      pages: 50,
      format: 'PDF',
      size: '4.2 MB',
      featured: true,
      image: 'https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?w=800&h=600&fit=crop'
    },
    {
      id: 2,
      title: 'Trademark Office Actions: Response Strategies',
      description: 'Expert strategies for responding to common USPTO office actions, with templates and real-world examples.',
      type: 'Whitepaper',
      pages: 28,
      format: 'PDF',
      size: '2.8 MB',
      featured: true,
      image: 'https://images.unsplash.com/photo-1568667256549-094345857637?w=800&h=600&fit=crop'
    },
    {
      id: 3,
      title: 'International Trademark Protection Handbook',
      description: 'Navigate the complexities of protecting your trademark globally with this essential handbook covering the Madrid Protocol and regional systems.',
      type: 'eBook',
      pages: 65,
      format: 'PDF',
      size: '5.1 MB',
      featured: false,
      image: 'https://images.unsplash.com/photo-1526304640581-d334cdbbf45e?w=800&h=600&fit=crop'
    },
    {
      id: 4,
      title: 'Trademark Enforcement Best Practices',
      description: 'Learn how to monitor, enforce, and defend your trademark rights with proven strategies from experienced attorneys.',
      type: 'Guide',
      pages: 32,
      format: 'PDF',
      size: '3.4 MB',
      featured: false,
      image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=800&h=600&fit=crop'
    },
    {
      id: 5,
      title: 'Trademark Valuation for Businesses',
      description: 'Understanding the financial value of your trademark assets and how to maximize their worth for business growth.',
      type: 'Whitepaper',
      pages: 22,
      format: 'PDF',
      size: '2.1 MB',
      featured: false,
      image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&h=600&fit=crop'
    },
    {
      id: 6,
      title: 'E-Commerce Trademark Protection Guide',
      description: 'Essential strategies for protecting your brand in the digital marketplace, including Amazon Brand Registry and social media.',
      type: 'Guide',
      pages: 38,
      format: 'PDF',
      size: '3.7 MB',
      featured: false,
      image: 'https://images.unsplash.com/photo-1556742502-ec7c0e9f34b1?w=800&h=600&fit=crop'
    },
    {
      id: 7,
      title: 'Trademark Portfolio Management',
      description: 'Best practices for managing multiple trademarks across different classes and jurisdictions efficiently.',
      type: 'eBook',
      pages: 45,
      format: 'PDF',
      size: '4.5 MB',
      featured: false,
      image: 'https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=800&h=600&fit=crop'
    },
    {
      id: 8,
      title: 'Trademark Search Methodology',
      description: 'Professional techniques for conducting thorough trademark searches to avoid conflicts and rejections.',
      type: 'Whitepaper',
      pages: 26,
      format: 'PDF',
      size: '2.6 MB',
      featured: false,
      image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&h=600&fit=crop'
    },
    {
      id: 9,
      title: 'Small Business Trademark Starter Kit',
      description: 'Everything a small business owner needs to know about trademark protection, simplified and actionable.',
      type: 'Guide',
      pages: 30,
      format: 'PDF',
      size: '3.0 MB',
      featured: false,
      image: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&h=600&fit=crop'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-[#FF6B35] via-[#FF8C42] to-[#FFA06B] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <BookOpen className="w-4 h-4" />
              Free Resources
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Publications & Resources
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              Download our comprehensive guides, whitepapers, and eBooks to master trademark protection
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Featured Publications */}
        <div className="mb-16">
          <div className="flex items-center gap-3 mb-8">
            <div className="h-1 w-12 bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] rounded-full"></div>
            <h2 className="text-3xl font-bold text-gray-900">Featured Publications</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {publications.filter(item => item.featured).map((pub) => (
              <article
                key={pub.id}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group"
              >
                <div className="grid md:grid-cols-2 gap-6 p-8">
                  <div className="relative h-64 rounded-xl overflow-hidden">
                    <img
                      src={pub.image}
                      alt={pub.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="bg-[#FF6B35] text-white px-3 py-1 rounded-lg text-sm font-semibold">
                        {pub.type}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col justify-between">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-[#FF6B35] transition-colors">
                        {pub.title}
                      </h3>
                      <p className="text-gray-600 mb-4">
                        {pub.description}
                      </p>
                    </div>

                    <div>
                      <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                        <div className="flex items-center gap-1">
                          <FileText className="w-4 h-4" />
                          <span>{pub.pages} pages</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Download className="w-4 h-4" />
                          <span>{pub.size}</span>
                        </div>
                      </div>

                      <div className="flex gap-3">
                        <button className="flex-1 bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] hover:from-[#FF8C42] hover:to-[#FF6B35] text-white font-semibold px-4 py-2 rounded-lg transition-all duration-300 flex items-center justify-center gap-2">
                          <Download className="w-4 h-4" />
                          Download
                        </button>
                        <button className="border-2 border-gray-200 hover:border-[#FF6B35] text-gray-700 hover:text-[#FF6B35] font-semibold px-4 py-2 rounded-lg transition-all duration-300 flex items-center justify-center gap-2">
                          <Eye className="w-4 h-4" />
                          Preview
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>

        {/* All Publications */}
        <div>
          <div className="flex items-center gap-3 mb-8">
            <div className="h-1 w-12 bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] rounded-full"></div>
            <h2 className="text-3xl font-bold text-gray-900">All Publications</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {publications.filter(item => !item.featured).map((pub) => (
              <article
                key={pub.id}
                className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={pub.image}
                    alt={pub.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-3 right-3">
                    <span className="bg-black/70 text-white px-2 py-1 rounded text-xs font-semibold">
                      {pub.type}
                    </span>
                  </div>
                </div>

                <div className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-[#FF6B35] transition-colors line-clamp-2">
                    {pub.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {pub.description}
                  </p>

                  <div className="flex items-center gap-4 text-xs text-gray-500 mb-4 pb-4 border-b border-gray-100">
                    <div className="flex items-center gap-1">
                      <FileText className="w-3 h-3" />
                      <span>{pub.pages}p</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="font-medium">{pub.format}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span>{pub.size}</span>
                    </div>
                  </div>

                  <button className="w-full bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] hover:from-[#FF8C42] hover:to-[#FF6B35] text-white font-semibold px-4 py-2 rounded-lg transition-all duration-300 flex items-center justify-center gap-2">
                    <Download className="w-4 h-4" />
                    Download Free
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Need Personalized Guidance?
          </h2>
          <p className="text-gray-300 mb-8">
            Talk to our trademark experts for customized advice on protecting your brand
          </p>
          <Link
            to="/contact"
            className="inline-block bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] hover:from-[#FF8C42] hover:to-[#FF6B35] text-white font-bold px-8 py-4 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg shadow-[#FF6B35]/30"
          >
            Schedule Free Consultation
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PublicationsPage;
